import React, {PropsWithChildren, ReactNode, useMemo, useState} from "react";
import {Box, FormHelperText, Typography} from "@material-ui/core";
import FfairModal from "view/components/modal/FfairModal";
import Form, {ActionButton, Actions, BasicSelect} from "view/components/Form";
import {ErrorType} from "ts/api/error";
import {useForm} from "react-hook-form";
import Alert from "view/components/Alert";
import RadioGroup from "view/components/RadioGroup";
import {AxiosError} from "axios";
import {useLocalisation} from "hooks/translation";
import {LocalisationTarget} from "ts/app/translation";
import {
  LocalisationProvider,
  LocalisationSection,
  LocalisationSettingsBar,
} from "view/components/Localisation";
import ModalField from "./ModalField";
import {EditModalProps, SettingValues} from "../types";
import useStyles from "./styles";
import getDirtyFields from "./getDirtyFields";

// TODO: Support different field types. Move 'multiselect' prop functionality to the ModalField component.
// Make the EditModal component more abstract
const EditModal = <T extends SettingValues>({
  onSubmit,
  handleClose,
  defaultValues,
  title,
  fields,
  multiselect,
  submitTitle,
  infoMessage,
  id,
  localisation,
}: PropsWithChildren<EditModalProps<T>>) => {
  const styles = useStyles();

  const form = useForm<any>({
    mode: "all",
    defaultValues,
  });
  const [submitError, setSubmitError] = useState<string>("");
  const {formState, handleSubmit, getValues, control} = form;

  const localisationTargets = useMemo<LocalisationTarget[]>(() => {
    if (!localisation) return [];

    const {entity, entityId} = localisation;

    return localisation.targets.map((target) => ({
      key: target.field,
      entity,
      entityId,
      field: target.field,
      label: target.label,
      kind: target.kind,
      maxLength: target.maxLength,
      getSourceText: () => getValues(target.sourceName ?? target.field) ?? "",
    }));
  }, [localisation, getValues]);

  const localisationController = useLocalisation({
    showId: localisation?.showId,
    targets: localisationTargets,
    enabled: !!localisation,
  });

  const submitHandler = async (data: T) => {
    try {
      setSubmitError("");

      const isLocalisationOnlySave =
        !!localisation && !Object.keys(data).length;
      const createdId = isLocalisationOnlySave
        ? undefined
        : await onSubmit(data);

      if (localisation) {
        await localisationController.save({
          resolveEntityId: () =>
            localisation.entityId ??
            (typeof createdId === "string" ? createdId : undefined),
        });
      }

      handleClose();
    } catch (e) {
      const axiosError = e as AxiosError;

      if (axiosError.response && axiosError?.response?.data?.message) {
        setSubmitError(axiosError?.response?.data?.message);
        return;
      }

      const typedError = e as ErrorType;
      if (typedError.message) {
        setSubmitError(typedError.message);
      }
    }
  };

  const renderLocalisationSection = (fieldName: string) => {
    const target = localisation?.targets.find(
      (item) => (item.sourceName ?? item.field) === fieldName
    );
    if (!target) return null;

    return (
      <LocalisationSection
        id={`${id ?? "settings"}-${target.field}`}
        targetKeys={[target.field]}
        richTextOptions={localisation?.richTextOptions}
      />
    );
  };

  const renderFields = () => {
    const renderError = (message: ReactNode) => (
      <Typography className={styles.errorText} color="error">
        {message}
      </Typography>
    );

    if (multiselect?.items?.length) {
      if (multiselect?.type === "radio") {
        return <RadioGroup items={multiselect.items} name={multiselect.name} />;
      }
      return (
        <BasicSelect
          label={String(multiselect.label || multiselect.title)}
          name={String(multiselect.name)}
          options={multiselect.items}
          defaultValue={multiselect.name}
        />
      );
    }

    if (fields) {
      if (typeof fields === "function") {
        const functionalFields = fields(form.watch());

        return functionalFields.map((field, index) => {
          // eslint-disable-next-line
          // @ts-ignore
          const fieldError: FieldError = formState.errors[`${field.name}`];
          const errorMessage = fieldError?.message;
          return (
            <div key={`${index}-${field.name}`}>
              <ModalField field={field} />
              {field.showErrorMessage &&
                errorMessage &&
                renderError(errorMessage as ReactNode)}
              {renderLocalisationSection(String(field.name))}
            </div>
          );
        });
      }

      return fields.map((field, index) => {
        if ("render" in field) {
          return field.render();
        }
        // eslint-disable-next-line
        // @ts-ignore
        const fieldError: FieldError = formState.errors[`${field.name}`];
        const errorMessage = fieldError?.message;

        return (
          <div key={`${index}-${field.name}`}>
            <ModalField
              field={field}
              defaultValue={getValues(field.name)}
              control={control}
            />
            {field.showErrorMessage &&
              errorMessage &&
              renderError(errorMessage as ReactNode)}
            {renderLocalisationSection(String(field.name))}
          </div>
        );
      });
    }
  };

  const renderInfoMessage = () => {
    return (
      <Alert severity="info" className={styles.info}>
        {infoMessage}
      </Alert>
    );
  };

  const open = true;
  return (
    <FfairModal onClose={handleClose} open={open}>
      <Box id={id} className={styles.paper}>
        <Typography variant="h2" className={styles.header}>
          {title}
        </Typography>
        <Form
          className={styles.form}
          form={form}
          onSubmit={handleSubmit((data) =>
            submitHandler(getDirtyFields(formState.dirtyFields, data) as T)
          )}
        >
          <LocalisationProvider controller={localisationController}>
            <LocalisationSettingsBar />
            {renderFields()}
            {infoMessage && renderInfoMessage()}
            <Box mt={1}>
              <FormHelperText error>{submitError}</FormHelperText>
            </Box>
            <Actions>
              <ActionButton onClick={handleClose} text="Cancel" />
              <ActionButton
                type="submit"
                text={submitTitle || "Save"}
                allowClean={localisationController.isDirty}
              />
            </Actions>
          </LocalisationProvider>
        </Form>
      </Box>
    </FfairModal>
  );
};

export default EditModal;
